// components/AssignTenantsToPlanModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Users,  Check} from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../../api';

const AssignTenantsToPlanModal = ({ 
  isOpen, 
  onClose, 
  selectedTenants, 
  plans,
  onSuccess 
}) => {
  const { success, error: showError } = useToast();
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privatePlans, setPrivatePlans] = useState([]);
  const [allTenants, setAllTenants] = useState([]);

  useEffect(() => {
    if (isOpen) {
      // Filter only private plans
      const filtered = plans.filter(p => p.visibility === 'private' && p.isActive);
      setPrivatePlans(filtered);
      if (filtered.length > 0) {
        setSelectedPlanId(filtered[0].planId);
      }
      loadTenants();
    }
  }, [isOpen, plans]);

  const loadTenants = async () => {
    try {
      const res = await api.get('/api/super-admin/subscription/tenants?limit=100');
      if (res.data.success) {
        setAllTenants(res.data.data || []);
      }
    } catch (err) {
      console.error('❌ Error loading tenants:', err);
    }
  };

  if (!isOpen) return null;

  const selectedPlan = plans.find(p => p.planId === selectedPlanId);

  const handleAssign = async () => {
    if (!selectedPlanId) {
      showError('Please select a plan');
      return;
    }

    if (!selectedTenants || selectedTenants.length === 0) {
      showError('No tenants selected');
      return;
    }

    setIsSubmitting(true);
    try {
      const promises = selectedTenants.map(tenantId => 
        api.post(`/api/super-admin/subscription/plans/${selectedPlanId}/assign-tenant`, {
          tenantId: tenantId,
          makePrivate: true
        })
      );

      await Promise.all(promises);
      
      success(`Successfully assigned ${selectedTenants.length} tenant(s) to the plan`);
      
      if (onSuccess) {
        await onSuccess();
      }
      
      onClose();
    } catch (err) {
      console.error('❌ Error assigning tenants:', err);
      showError(err.response?.data?.message || 'Failed to assign tenants');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnassign = async (tenantId, planId) => {
    if (!window.confirm('Are you sure you want to remove this tenant from the plan?')) return;
    
    setIsSubmitting(true);
    try {
      await api.delete(`/api/super-admin/subscription/plans/${planId}/remove-tenant`, {
        data: { tenantId: tenantId }
      });
      
      success('Tenant removed from plan successfully!');
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      console.error('❌ Error unassigning tenant:', err);
      showError(err.response?.data?.message || 'Failed to remove tenant');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTenantName = (tenantId) => {
    const tenant = allTenants.find(t => t.tenantId === tenantId);
    return tenant?.organizationName || tenantId;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Assign Tenants to Private Plan</h2>
            <p className="text-sm text-slate-500">{selectedTenants?.length || 0} tenant(s) selected</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Selected Tenants Preview */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Selected Tenants</label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-slate-50 rounded-xl border border-slate-200">
              {selectedTenants?.map((tenantId) => (
                <span key={tenantId} className="px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-700">
                  {getTenantName(tenantId)}
                </span>
              ))}
            </div>
          </div>

          {/* Plan Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Select Private Plan</label>
            {privatePlans.length === 0 ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
                <p className="text-sm text-amber-700">No private plans available. Create a private plan first.</p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-2 px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Create Private Plan
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {privatePlans.map((plan) => {
                  const assignedTenants = plan.assignedTenants || [];
                  const isSelected = selectedPlanId === plan.planId;
                  
                  return (
                    <button
                      key={plan.planId}
                      type="button"
                      onClick={() => setSelectedPlanId(plan.planId)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                          : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
                      }`}
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-900">{plan.name}</p>
                        <p className="text-xs text-slate-500">
                          {assignedTenants.length} tenant(s) assigned
                        </p>
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-indigo-600" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Plan Details with Assigned Tenants */}
          {selectedPlan && (
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-xs text-slate-500">Selected Plan</p>
              <p className="text-sm font-medium text-slate-900">{selectedPlan.name}</p>
              <p className="text-xs text-slate-500">
                Price: {selectedPlan.pricing?.yearly?.amount === 0 ? 'Free' : `₹${selectedPlan.pricing?.yearly?.amount}/year`}
                {selectedPlan.trial?.enabled && ` • ${selectedPlan.trial.durationDays} day trial`}
              </p>
              
              {/* ✅ Show Already Assigned Tenants with Unassign */}
              {(selectedPlan.assignedTenants?.length > 0) && (
                <div className="mt-2 pt-2 border-t border-slate-200">
                  <p className="text-xs font-medium text-slate-600 mb-1">
                    Assigned Tenants ({selectedPlan.assignedTenants.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedPlan.assignedTenants.map((tenantId) => (
                      <span
                        key={tenantId}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700"
                      >
                        ✅ {getTenantName(tenantId)}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnassign(tenantId, selectedPlan.planId);
                          }}
                          className="text-emerald-400 hover:text-red-500 transition-colors ml-0.5"
                          title="Remove from plan"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <button
            type="button"
            onClick={handleAssign}
            disabled={isSubmitting || privatePlans.length === 0 || selectedTenants.length === 0}
            className="flex-1 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Users className="w-4 h-4" />
            {isSubmitting ? 'Processing...' : `Assign ${selectedTenants?.length || 0} Tenant(s)`}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignTenantsToPlanModal;